import React, { useState, useEffect, useRef } from "react";
import { X, ArrowLeft, Sparkles, Loader2, FileText, Type } from "lucide-react";
import axios from "axios";
import { aiAPI } from "../utils/api";
import { useSocket } from "../contexts/SocketContext";
import { useNotification } from "../contexts/NotificationContext";
import PDFUploadStep from "./AIExtract/PDFUploadStep";
import TextInputStep from "./AIExtract/TextInputStep";
import ProcessingStep from "./AIExtract/ProcessingStep";
import TicketReviewStep from "./AIExtract/TicketReviewStep";

const AIExtractModal = ({ projectId, onClose, onCardsCreated }) => {
  const [activeTab, setActiveTab] = useState("pdf"); // 'pdf' | 'text'
  const [step, setStep] = useState("upload"); // 'upload' | 'processing' | 'review' | 'creating'
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [extractedTickets, setExtractedTickets] = useState([]);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [fileProgressMap, setFileProgressMap] = useState({});
  const [totalFiles, setTotalFiles] = useState(0);
  const [extractionId, setExtractionId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);

  const { socket } = useSocket();
  const { showToast } = useNotification();
  const modalRef = useRef(null);

  // Socket.IO listeners
  useEffect(() => {
    if (!socket) return;

    const handleFileStart = (data) => {
      setFileProgressMap((prev) => ({
        ...prev,
        [data.fileId]: {
          fileId: data.fileId,
          fileName: data.fileName,
          status: "processing",
          progress: 0,
          message: "Starting...",
        },
      }));
    };

    const handleProgress = (data) => {
      setFileProgressMap((prev) => ({
        ...prev,
        [data.fileId]: {
          ...prev[data.fileId],
          progress: data.progress,
          message: data.message,
          status: "processing",
        },
      }));
    };

    const handleFileComplete = (data) => {
      setFileProgressMap((prev) => ({
        ...prev,
        [data.fileId]: {
          ...prev[data.fileId],
          status: "completed",
          progress: 100,
          ticketsCount: data.tickets?.length || 0,
        },
      }));
    };

    const handleFileSkipped = (data) => {
      setFileProgressMap((prev) => ({
        ...prev,
        [data.fileId]: {
          fileId: data.fileId,
          fileName: data.fileName,
          status: "skipped",
          reason: data.reason,
        },
      }));
    };

    const handleFileError = (data) => {
      setFileProgressMap((prev) => ({
        ...prev,
        [data.fileId]: {
          ...prev[data.fileId],
          status: "failed",
          error: data.message,
        },
      }));
    };

    const handleComplete = (data) => {
      setExtractedTickets(data.tickets || []);
      setDuplicateInfo(data.results?.flatMap((r) => r.duplicateInfo || []) || []);
      setTimeout(() => setStep("review"), 500);
    };

    const handleError = (data) => {
      showToast(data.message || "Document extraction failed. Please try again", "error");
      setStep("upload");
      setUploadedFiles([]);
      setFileProgressMap({});
    };

    socket.on("ai-extraction-file-start", handleFileStart);
    socket.on("ai-extraction-progress", handleProgress);
    socket.on("ai-extraction-file-complete", handleFileComplete);
    socket.on("ai-extraction-file-skipped", handleFileSkipped);
    socket.on("ai-extraction-file-error", handleFileError);
    socket.on("ai-extraction-complete", handleComplete);
    socket.on("ai-extraction-error", handleError);

    return () => {
      socket.off("ai-extraction-file-start", handleFileStart);
      socket.off("ai-extraction-progress", handleProgress);
      socket.off("ai-extraction-file-complete", handleFileComplete);
      socket.off("ai-extraction-file-skipped", handleFileSkipped);
      socket.off("ai-extraction-file-error", handleFileError);
      socket.off("ai-extraction-complete", handleComplete);
      socket.off("ai-extraction-error", handleError);
    };
  }, [socket, showToast]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleUploadAndExtract = async () => {
    // Validate input based on active tab
    if (activeTab === "pdf") {
      if (!uploadedFiles || uploadedFiles.length === 0) {
        showToast("Please select at least one PDF file to continue", "error");
        return;
      }
    } else if (activeTab === "text") {
      if (!textInput || textInput.trim().length < 100) {
        showToast("Please enter at least 100 characters to continue", "error");
        return;
      }
      if (textInput.length > 50000) {
        showToast("Text input exceeds maximum length of 50,000 characters", "error");
        return;
      }
    }

    setLoading(true);
    setStep("processing");

    if (activeTab === "pdf") {
      setTotalFiles(uploadedFiles.length);

      // Initialize progress map for all files
      const initialProgressMap = {};
      uploadedFiles.forEach((file, index) => {
        initialProgressMap[`file-${index}`] = {
          fileId: `file-${index}`,
          fileName: file.name,
          status: "pending",
          progress: 0,
        };
      });
      setFileProgressMap(initialProgressMap);
    } else {
      // For text input, show single processing item
      setTotalFiles(1);
      setFileProgressMap({
        "text-input": {
          fileId: "text-input",
          fileName: "Text Input",
          status: "processing",
          progress: 0,
          message: "Extracting tickets from text...",
        },
      });
    }

    try {
      let response;

      if (activeTab === "pdf") {
        const formData = new FormData();
        uploadedFiles.forEach((fileObj) => {
          formData.append("file", fileObj.file);
        });
        response = await aiAPI.extractTicketsFromPDF(projectId, formData);
      } else {
        response = await aiAPI.extractTicketsFromText(projectId, { text: textInput });
      }

      if (response.data.success) {
        const { tickets, results, columns } = response.data.data;

        setExtractedTickets(tickets || []);
        setColumns(columns || []);

        if (activeTab === "text") {
          // Update progress for text input
          setFileProgressMap({
            "text-input": {
              fileId: "text-input",
              fileName: "Text Input",
              status: "completed",
              progress: 100,
              ticketsCount: tickets?.length || 0,
            },
          });
        }

        if (tickets.length === 0) {
          showToast("No tickets were found in the provided content", "warning");
          setStep("upload");
          setUploadedFiles([]);
          setTextInput("");
          setFileProgressMap({});
        } else {
          showToast(response.data.message, "success");
          // Socket.IO will trigger the transition to review step for PDF
          // For text, transition immediately after short delay
          if (activeTab === "text") {
            setTimeout(() => setStep("review"), 500);
          }
        }
      }
    } catch (error) {
      console.error("Extraction error:", error);
      console.error("Error response:", error.response);

      // Handle request cancellation/timeout
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        showToast(
          "Request timed out. AI processing is taking longer than expected. Please try with shorter content or fewer files.",
          "error"
        );
      } else if (error.response?.status === 409) {
        showToast(
          error.response.data.message || "This content has already been processed",
          "error"
        );
      } else if (error.response?.status === 400) {
        showToast(
          error.response.data.message || "Unable to process the provided content",
          "error"
        );
      } else if (error.response?.status === 503) {
        showToast(
          error.response?.data?.message || "AI service is temporarily unavailable. Please try again in a few moments",
          "error"
        );
      } else if (axios.isCancel(error)) {
        showToast("Request was canceled", "warning");
      } else {
        // Show the actual error message from backend if available
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || "Unable to extract tickets. Please try again";
        showToast(errorMessage, "error");
      }

      setStep("upload");
      setUploadedFiles([]);
      setTextInput("");
      setFileProgressMap({});
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCards = async () => {
    const validTickets = extractedTickets.filter(
      (ticket) => ticket.title.trim().length > 0
    );

    if (validTickets.length === 0) {
      showToast("Please ensure all tickets have a title before creating cards", "error");
      return;
    }

    setLoading(true);
    setStep("creating");

    try {
      // Create minimal ticket references instead of sending full data
      const ticketRefs = validTickets.map(ticket => ({
        ticketId: ticket.ticketId,
        extractionId: ticket.extractionId,
        // Only include fields that may have been modified by user
        title: ticket.title,
        description: ticket.description,
        priority: ticket.priority,
        status: ticket.status,
      }));

      const response = await aiAPI.createCardsFromTickets(projectId, ticketRefs);

      if (response.data.success) {
        showToast(response.data.message, "success");
        onCardsCreated(response.data.data.cards);
        onClose();
      }
    } catch (error) {
      console.error("Create cards error:", error);
      showToast(
        error.response?.data?.message || "Unable to create cards from the selected tickets. Please try again",
        "error"
      );
      setStep("review");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === "review") {
      setStep("upload");
      setUploadedFiles([]);
      setTextInput("");
      setExtractedTickets([]);
      setDuplicateInfo([]);
      setFileProgressMap({});
    }
  };

  const getStepIndicator = () => {
    const steps = [
      { key: "upload", label: "Upload" },
      { key: "processing", label: "Process" },
      { key: "review", label: "Review" },
    ];

    const isStepComplete = (stepKey) => {
      if (step === "review" && stepKey !== "review") return true;
      if ((step === "creating" || step === "review") && stepKey === "processing") return true;
      return false;
    };

    const isStepActive = (stepKey) => {
      if (step === stepKey) return true;
      if (step === "creating" && stepKey === "review") return true;
      return false;
    };

    return (
      <div className="flex items-center justify-center mb-8">
        {steps.map((s, index) => (
          <React.Fragment key={s.key}>
            <div className="flex items-center">
              <div
                className={`flex items-center gap-2 ${
                  isStepActive(s.key) ? "text-blue-600 dark:text-blue-400" :
                  isStepComplete(s.key) ? "text-green-600 dark:text-green-400" :
                  "text-gray-400 dark:text-slate-500"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    isStepActive(s.key) ? "bg-blue-600 text-white" :
                    isStepComplete(s.key) ? "bg-green-600 text-white" :
                    "bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-400"
                  }`}
                >
                  {isStepComplete(s.key) ? "✓" : index + 1}
                </div>
                <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className={`w-16 h-0.5 mx-2 ${
                isStepComplete(steps[index + 1].key) ? "bg-green-600 dark:bg-green-500" : "bg-gray-300 dark:bg-slate-700"
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-colors duration-200">
      <div
        ref={modalRef}
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col transition-colors duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-[61.19px] min-h-[61.19px] max-h-[61.19px] bg-[#F1F2F4] dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shrink-0 box-border rounded-t-lg transition-colors duration-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 min-w-[32px] min-h-[32px] bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white leading-tight">
                AI Ticket Extraction
              </h2>
              <p className="text-xs text-gray-500 dark:text-slate-400 leading-tight">
                Extract tickets from documents
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center rounded-lg hover:bg-gray-400/20 transition-colors text-gray-600 dark:text-slate-400"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-slate-800 transition-colors duration-200">
          {getStepIndicator()}

          {step === "upload" && (
            <>
              {/* Tab Navigation */}
              <div className="flex gap-2 mb-6 bg-gray-100 dark:bg-slate-900 p-1 rounded-lg transition-colors duration-200">
                <button
                  onClick={() => setActiveTab("pdf")}
                  className={`flex-1 h-8 flex items-center justify-center gap-2 px-4 rounded-md font-medium text-xs transition-all ${
                    activeTab === "pdf"
                      ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>Upload PDF</span>
                </button>
                <button
                  onClick={() => setActiveTab("text")}
                  className={`flex-1 h-8 flex items-center justify-center gap-2 px-4 rounded-md font-medium text-xs transition-all ${
                    activeTab === "text"
                      ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                  }`}
                >
                  <Type className="w-3.5 h-3.5" />
                  <span>Text Input</span>
                </button>
              </div>

              {/* Render step based on active tab */}
              {activeTab === "pdf" ? (
                <PDFUploadStep
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  isDragOver={isDragOver}
                  setIsDragOver={setIsDragOver}
                  showToast={showToast}
                />
              ) : (
                <TextInputStep
                  textInput={textInput}
                  setTextInput={setTextInput}
                  showToast={showToast}
                />
              )}
            </>
          )}

          {step === "processing" && (
            <ProcessingStep
              fileProgressMap={fileProgressMap}
              totalFiles={totalFiles}
            />
          )}

          {step === "review" && (
            <TicketReviewStep
              tickets={extractedTickets}
              setTickets={setExtractedTickets}
              duplicateInfo={duplicateInfo}
              columns={columns}
            />
          )}

          {step === "creating" && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                Creating Cards
              </p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                This will only take a moment
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-slate-700 px-6 py-3 flex justify-between items-center flex-shrink-0 bg-gray-50 dark:bg-slate-900/50 transition-colors duration-200">
          <div>
            {step === "review" && (
              <button
                onClick={handleBack}
                className="h-8 flex items-center gap-1.5 px-3 text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700 font-medium text-xs rounded-lg transition-colors"
                disabled={loading}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back</span>
              </button>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="h-8 px-4 flex items-center justify-center text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 font-medium text-xs rounded-lg transition-colors border border-gray-300 dark:border-slate-600"
              disabled={loading}
            >
              Cancel
            </button>

            {step === "upload" && (
              <button
                onClick={handleUploadAndExtract}
                disabled={
                  (activeTab === "pdf" && uploadedFiles.length === 0) ||
                  (activeTab === "text" && (textInput.trim().length < 100 || textInput.length > 50000)) ||
                  loading
                }
                className="h-8 bg-blue-600 text-white hover:bg-blue-700 font-medium px-4 text-xs rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>
                  {activeTab === "pdf"
                    ? `Extract${uploadedFiles.length > 0 ? ` (${uploadedFiles.length})` : ""}`
                    : "Extract"}
                </span>
              </button>
            )}

            {step === "review" && (
              <button
                onClick={handleCreateCards}
                disabled={
                  loading ||
                  extractedTickets.length === 0 ||
                  extractedTickets.filter((t) => t.title.trim().length > 0)
                    .length === 0
                }
                className="h-8 bg-blue-600 text-white hover:bg-blue-700 font-medium px-4 text-xs rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>
                  Create{" "}
                  {
                    extractedTickets.filter((t) => t.title.trim().length > 0)
                      .length
                  }{" "}
                  {extractedTickets.filter((t) => t.title.trim().length > 0).length === 1 ? "Card" : "Cards"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIExtractModal;
